<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <loading :status="loading"/>

        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <h1 class="stage-page-title truncate">{{ $t('Secure note') }}</h1>
                </div>
                <div class="stage-page-header-actions" v-on-clickaway="closeActionDropdowns">
                    <button class="btn-stage-icon-plain" type="button" :title="$t('Back')" :aria-label="$t('Back')" @click="goBackToVaultList">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.chevron-left"></svg-vue>
                    </button>
                    <div v-if="canManagePermissions && !isCustomer" class="relative inline-flex items-center">
                        <button
                            class="btn-stage-icon-plain"
                            type="button"
                            title="Freigabeverwaltung"
                            aria-label="Freigabeverwaltung"
                            @click="openApprovalModal"
                        >
                            <svg-vue class="h-4 w-4" icon="font-awesome.regular.user-lock"></svg-vue>
                        </button>
                    </div>
                    <div class="relative inline-flex items-center">
                        <button
                            class="btn-stage-icon-plain"
                            type="button"
                            title="Verlauf"
                            aria-label="Verlauf"
                            @click="toggleHistoryPopover"
                        >
                            <svg-vue class="h-4 w-4" icon="font-awesome.regular.rectangle-history"></svg-vue>
                        </button>
                        <div
                            v-show="historyPopoverOpen"
                            class="vault-history-popover origin-top-right absolute top-full right-0 mt-2 w-[22rem] rounded shadow-lg z-50 bg-white border border-bordergray-100 overflow-hidden"
                        >
                            <div class="stage-icon-menu-header">
                                {{ $t('Activity history') }}
                            </div>
                            <div class="max-h-80 overflow-y-auto">
                                <template v-if="historyEntries.length > 0">
                                    <div
                                        v-for="entry in historyEntries"
                                        :key="'vault-history-' + entry.id"
                                        class="px-4 py-3 border-b border-bordergray-100 last:border-b-0"
                                    >
                                        <div class="flex items-center">
                                            <img
                                                :src="resolveAvatar(entry.user)"
                                                alt="Profilbild"
                                                class="h-6 w-6 rounded-full object-cover mr-2"
                                            />
                                            <div class="min-w-0 w-full flex items-center justify-between gap-3">
                                                <div class="min-w-0">
                                                    <div class="text-xs font-medium text-gray-900 truncate pr-1">
                                                        {{ entry.user.name }}
                                                    </div>
                                                    <div class="mt-0.5 text-xs text-gray-500 truncate">
                                                        {{ historyAccessType(entry) }}
                                                    </div>
                                                </div>
                                                <div class="text-xs text-gray-400 whitespace-nowrap self-center">
                                                    {{ formatDateTime(entry.at) }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <div v-else class="px-4 py-3 text-sm text-gray-500">
                                    {{ $t('No activities yet') }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="canEdit" class="relative inline-flex items-center">
                        <button
                            class="btn-stage-icon-plain"
                            type="button"
                            title="Bearbeiten"
                            aria-label="Bearbeiten"
                            @click="openTitleModal"
                        >
                            <svg-vue class="h-4 w-4" icon="font-awesome.regular.pen-to-square"></svg-vue>
                        </button>
                    </div>
                    <div v-if="canDelete" class="relative inline-flex items-center">
                        <button
                            class="btn-stage-icon-plain"
                            type="button"
                            title="Notiz löschen"
                            aria-label="Notiz löschen"
                            @click="openDeleteConfirmationModal"
                        >
                            <svg-vue class="h-4 w-4" icon="font-awesome.trash-alt-regular"></svg-vue>
                        </button>
                    </div>
                    <button
                        class="btn-stage-icon-plain"
                        type="button"
                        :title="$t('Reload')"
                        :aria-label="$t('Reload')"
                        @click="loadVault"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrows-repeat"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-gutters stage-page-fill pt-3 h-0">
            <div class="w-full h-full bg-white shadow sm:rounded border border-bordergray-100 overflow-hidden">
                <div class="flex h-full min-h-0 flex-col">
                    <div class="flex flex-auto min-h-0 items-stretch">
                        <div class="flex-auto min-w-0 overflow-hidden flex flex-col">
                            <div class="sticky top-0 z-10 vault-editor-header">
                                <div class="flex items-stretch min-h-12">
                                    <div class="w-14 flex-shrink-0 vault-editor-header-gutter px-2 py-2 flex items-center justify-center text-gray-400">
                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.shield-keyhole"></svg-vue>
                                    </div>
                                    <div class="flex-1 min-w-0 flex items-center px-4 text-sm leading-5 font-medium text-gray-700 truncate">
                                        {{ vault.title || '-' }}
                                    </div>
                                    <div v-if="canManagePermissions" class="w-14 flex-shrink-0 border-l border-bordergray-100">
                                        <button
                                            class="inline-flex h-full w-full cursor-pointer items-center justify-center text-gray-600 transition-colors hover:text-gray-800 focus:outline-none"
                                            type="button"
                                            title="Notiz sicher versenden"
                                            aria-label="Notiz sicher versenden"
                                            @click="openSecureShareModal"
                                        >
                                            <svg-vue class="h-4 w-4" icon="font-awesome.regular.share"></svg-vue>
                                        </button>
                                    </div>
                                    <div v-if="canEdit" class="w-36 flex-shrink-0 vault-editor-header-save">
                                        <button class="btn-stage-primary w-full h-full rounded-none" style="border-radius: 0;" type="button" @click="saveVaultEntry">
                                            Speichern
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div class="vault-editor-panel">
                                <div class="vault-editor-shell">
                                    <div ref="lineNumberGutter" class="vault-editor-gutter">
                                        <div v-for="line in lineNumbers" :key="'vault-line-' + line" class="vault-editor-line-number">
                                            {{ line }}
                                        </div>
                                    </div>
                                    <textarea
                                        ref="vaultEditor"
                                        v-model="vaultSecretText"
                                        :readonly="!canEdit"
                                        class="vault-editor-textarea"
                                        spellcheck="false"
                                        wrap="off"
                                        @scroll="syncEditorScroll"
                                    ></textarea>
                                </div>
                            </div>

                            <div v-if="vault.active_secure_share" class="vault-secure-share-panel bg-white">
                                <div class="overflow-auto">
                                    <table class="table-listview w-full">
                                        <thead>
                                        <tr>
                                            <th class="table-listview-l-th table-listview-l-th-sticky whitespace-nowrap">
                                                Sicherer Versand an
                                            </th>
                                            <th class="table-listview-l-th table-listview-l-th-sticky w-56 whitespace-nowrap">
                                                Gültig bis
                                            </th>
                                            <th class="table-listview-l-th table-listview-l-th-sticky table-listview-l-th-right w-24 whitespace-nowrap">
                                                Aktion
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr>
                                            <td class="table-listview-l-td align-middle">
                                                <div class="text-sm text-gray-900 truncate">{{ vault.active_secure_share.recipient_email }}</div>
                                            </td>
                                            <td class="table-listview-l-td align-middle whitespace-nowrap">
                                                <div class="text-sm text-gray-500">{{ formatDateTime(vault.active_secure_share.expires_at) }}</div>
                                            </td>
                                            <td class="table-listview-l-td align-middle text-right">
                                                <button
                                                    v-if="canManagePermissions"
                                                    class="inline-flex items-center justify-center text-gray-500 hover:text-gray-700 focus:outline-none"
                                                    type="button"
                                                    :disabled="revokingSecureShare"
                                                    @click="revokeActiveSecureShare"
                                                >
                                                    <svg-vue class="h-4 w-4" icon="font-awesome.trash-alt-regular"></svg-vue>
                                                </button>
                                                <span v-else class="text-sm text-gray-400">-</span>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <aside v-if="!isCustomer" :style="{flex: '0 0 300px', width: '300px'}" class="hidden xl:block self-stretch bg-white border-l border-bordergray-100 overflow-y-auto">
                            <div class="h-full">
                                <div class="mx-6 pt-6 pb-6 mb-6 border-b border-bordergray-100">
                                    <div class="flex items-center">
                                        <img
                                            :alt="$t('Avatar')"
                                            :src="vaultOwnerAvatar"
                                            class="h-14 w-14 rounded-full object-cover"
                                        />
                                        <div class="ml-3 min-w-0 flex-1 self-center">
                                            <div class="mb-2 font-semibold text-base text-gray-800 truncate">{{ vault.owner || '-' }}</div>
                                            <div class="mb-1 text-sm leading-4 text-gray-500 truncate">
                                                {{ vault.owner_email || '-' }}
                                            </div>
                                            <div v-if="vault.owner_phone" class="text-sm leading-4 text-gray-500 truncate">
                                                {{ vault.owner_phone }}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="vaultCustomerReleaseUsers.length > 0" class="px-6 pb-6">
                                    <div class="text-xs tracking-wide text-gray-500 mb-2">Kundenfreigabe</div>
                                    <div class="space-y-4">
                                        <template v-for="person in vaultCustomerReleaseUsers" :key="'vault-customer-release-' + person.id">
                                            <div class="flex items-start">
                                                <div class="mr-3 flex-shrink-0">
                                                    <img
                                                        :alt="$t('Avatar')"
                                                        :src="person.avatar"
                                                        class="h-9 w-9 rounded-full object-cover"
                                                    />
                                                </div>
                                                <div class="min-w-0 flex-1">
                                                    <div class="text-gray-800 font-regular text-sm truncate w-full">{{ person.name }}</div>
                                                    <div class="text-sm leading-4 text-gray-500 truncate">{{ person.email || '-' }}</div>
                                                </div>
                                            </div>
                                        </template>
                                    </div>
                                </div>

                                <div v-if="vaultInternalReleaseUsers.length > 0" class="px-6 pb-6">
                                    <div class="text-xs tracking-wide text-gray-500 mb-2">Interne Freigabe</div>
                                    <div class="space-y-4">
                                        <template v-for="person in vaultInternalReleaseUsers" :key="'vault-internal-release-' + person.id">
                                            <div class="flex items-start">
                                                <div class="mr-3 flex-shrink-0">
                                                    <img
                                                        :alt="$t('Avatar')"
                                                        :src="person.avatar"
                                                        class="h-9 w-9 rounded-full object-cover"
                                                    />
                                                </div>
                                                <div class="min-w-0 flex-1">
                                                    <div class="text-gray-800 font-regular text-sm truncate w-full">{{ person.name }}</div>
                                                    <div class="text-sm leading-4 text-gray-500 truncate">{{ person.email || '-' }}</div>
                                                </div>
                                            </div>
                                        </template>
                                    </div>
                                </div>

                                <div v-if="vaultGuestReleaseUsers.length > 0" class="px-6 pb-6">
                                    <div class="text-xs tracking-wide text-gray-500 mb-2">Gastfreigabe</div>
                                    <div class="space-y-4">
                                        <template v-for="person in vaultGuestReleaseUsers" :key="'vault-guest-release-' + person.id">
                                            <div class="flex items-start">
                                                <div class="mr-3 flex-shrink-0">
                                                    <img
                                                        :alt="$t('Avatar')"
                                                        :src="person.avatar"
                                                        class="h-9 w-9 rounded-full object-cover"
                                                    />
                                                </div>
                                                <div class="min-w-0 flex-1">
                                                    <div class="text-gray-800 font-regular text-sm truncate w-full">{{ person.name }}</div>
                                                    <div class="text-sm leading-4 text-gray-500 truncate">{{ person.email || '-' }}</div>
                                                </div>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </div>
        </div>

        <approval-management-dialog
            :open="approvalModal"
            :internal-users="readerOptions"
            :customer-users="customerOptions"
            :guest-users="guestOptions"
            :selected-internal-ids="vault.reader_ids"
            :selected-customer-ids="vault.customer_ids"
            :selected-guest-ids="vault.guest_ids"
            :show-customer-tab="isAdmin"
            :show-guest-tab="isAdmin"
            @close="closeApprovalModal"
            @save="saveApprovalSelections"
        />

        <app-dialog
            :open="unlockModalOpen"
            title="Einmal Code eingeben"
            size="sm"
            :busy="unlockSubmitting"
            @close="closeUnlockModal"
        >
            <div class="vault-otp-group" @paste="handleCodePaste">
                <input
                    v-for="(digit, index) in unlockCodeDigits"
                    :key="'manage-unlock-digit-' + index"
                    ref="unlockCodeInput"
                    v-model="unlockCodeDigits[index]"
                    autocomplete="one-time-code"
                    inputmode="numeric"
                    maxlength="1"
                    type="text"
                    class="vault-otp-input"
                    :disabled="unlockSubmitting"
                    @input="handleCodeInput(index, $event)"
                    @keydown="handleCodeKeydown(index, $event)"
                >
            </div>
            <p class="mt-2 text-xs text-gray-500" aria-live="polite">
                Der Code ist 10 Minuten gültig<span v-if="unlockCodeCooldownRemaining > 0"> und kann in {{ unlockCodeCooldownRemaining }} Sek. neu angefordert werden</span>.
            </p>
            <div v-if="unlockError" class="mt-3 px-3 py-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded text-left">
                {{ unlockError }}
            </div>

            <template #footer>
                <div class="flex min-w-0 flex-1 items-center px-6">
                    <img :src="currentUserAvatar" alt="Profilbild" class="h-6 w-6 rounded-full object-cover mr-2 flex-shrink-0">
                    <div class="min-w-0 truncate text-xs text-gray-500">{{ currentUserEmail || 'eingeloggter Nutzer' }}</div>
                </div>
                <app-dialog-action
                    label="Code erhalten"
                    :disabled="sendingUnlockCode || unlockCodeCooldownRemaining > 0"
                    @click="sendUnlockCodeByEmail"
                />
            </template>
        </app-dialog>

        <confirm-dialog
            :open="deleteNoteModalOpen"
            :title="$t('Delete note').toString()"
            :text="$t('Do you really want to delete this note? This action cannot be undone.').toString()"
            confirm-label="Löschen"
            confirm-variant="danger"
            headline-id="delete-note-modal-headline"
            @close="deleteNoteModalOpen = false"
            @confirm="deleteNote"
        />

        <app-dialog
            :open="editDetailsModalOpen"
            title="Notizdetails bearbeiten"
            size="md"
            :busy="editDetailsSubmitting"
            @close="closeEditDetailsModal"
        >
            <form id="vault-details-dialog-form" class="grid grid-cols-1 gap-6" @submit.prevent="saveVaultDetails">
                <div>
                    <label class="block text-sm font-medium leading-5 text-gray-700" for="vault-detail-title">Titel</label>
                    <div class="mt-1">
                        <input
                            id="vault-detail-title"
                            ref="editDetailsTitleInput"
                            v-model.trim="editDetailsForm.title"
                            :disabled="!canManageDetails"
                            class="form-input block w-full"
                            maxlength="255"
                            type="text"
                        >
                    </div>
                </div>
                <div>
                    <label class="block text-sm font-medium leading-5 text-gray-700" for="vault-detail-description">Kurzbeschreibung (keine vertraulichen Daten)</label>
                    <div class="mt-1">
                        <textarea
                            id="vault-detail-description"
                            v-model.trim="editDetailsForm.short_description"
                            class="form-textarea block w-full"
                            rows="5"
                        ></textarea>
                    </div>
                </div>
                <p v-if="editDetailsError" class="text-sm text-red-600">{{ editDetailsError }}</p>
            </form>
            <template #footer>
                <app-dialog-action
                    form="vault-details-dialog-form"
                    native-type="submit"
                    label="Details speichern"
                    loading-label="Wird gespeichert..."
                    :loading="editDetailsSubmitting"
                />
            </template>
        </app-dialog>

        <app-dialog
            :open="secureShareModalOpen"
            title="Sicherer Versand"
            size="md"
            :busy="secureShareSubmitting"
            @close="closeSecureShareModal"
        >
            <form id="vault-share-dialog-form" class="grid grid-cols-1 gap-6" @submit.prevent="sendSecureShareLink">
                <p class="text-sm leading-5 text-gray-500">
                    {{ $t('The recipient receives a one-time usable link by email') }}.
                </p>
                <div>
                    <label class="block text-sm font-medium leading-5 text-gray-700" for="manage-share-recipient-email">{{ $t('Recipient email') }}</label>
                    <div class="mt-1">
                        <input
                            id="manage-share-recipient-email"
                            ref="secureShareEmailInput"
                            v-model.trim="secureShareForm.recipient_email"
                            class="form-input block w-full"
                            required
                            type="email"
                        >
                    </div>
                </div>
                <div>
                    <label class="block text-sm font-medium leading-5 text-gray-700" for="manage-share-expiry">{{ $t('Link valid for') }}</label>
                    <div class="mt-1">
                        <select id="manage-share-expiry" v-model="secureShareForm.expires_in" class="form-select block w-full">
                            <option value="1h">1 Stunde</option>
                            <option value="1d">1 Tag</option>
                            <option value="3d">3 Tage</option>
                            <option value="7d">7 Tage</option>
                            <option value="1m">1 Monat</option>
                        </select>
                    </div>
                </div>
                <p class="text-sm text-gray-500">
                    {{ $t('The link can only be used once and becomes invalid immediately on first access') }}.
                </p>
                <p v-if="secureShareError" class="text-sm text-red-600">{{ secureShareError }}</p>
            </form>
            <template #footer>
                <app-dialog-action
                    form="vault-share-dialog-form"
                    native-type="submit"
                    label="Sicher versenden"
                    loading-label="Wird gesendet..."
                    :loading="secureShareSubmitting"
                />
            </template>
        </app-dialog>
    </div>
</template>

<script>
import ApprovalManagementDialog from "@/components/modals/approval-management-dialog";
import AppDialog from "@/components/modals/app-dialog.vue";
import AppDialogAction from "@/components/modals/app-dialog-action.vue";
import ConfirmDialog from "@/components/modals/confirm-dialog.vue";
import {mixin as clickaway} from "vue-clickaway";
import {resolveAccessState} from "@/utils/access-state";
import {resolveReturnToPath} from "@/utils/list-route-state";
import {
    buildVaultReleaseUsers,
    createDefaultVaultState,
    formatVaultDateTime,
    normalizeVaultState,
    resolveVaultAvatar,
    resolveVaultHistoryAccessType,
} from "@/utils/workspace-vault-view";

export default {
    name: "workspace-vault-manage",
    components: {
        ApprovalManagementDialog,
        AppDialog,
        AppDialogAction,
        ConfirmDialog,
    },
    mixins: [clickaway],
    metaInfo() {
        return {
            title: this.$t('Secure note')
        };
    },
    data() {
        return {
            loading: false,
            historyPopoverOpen: false,
            approvalModal: false,
            deleteNoteModalOpen: false,
            editDetailsModalOpen: false,
            secureShareModalOpen: false,
            unlockModalOpen: false,
            unlockCodeDigits: ['', '', '', ''],
            unlockError: '',
            nowTick: Date.now(),
            unlockCodeCooldownUntil: 0,
            sendingUnlockCode: false,
            unlockSubmitting: false,
            clockIntervalId: null,
            savingEntry: false,
            revokingSecureShare: false,
            lockingVaultOnLeave: false,
            editDetailsSubmitting: false,
            editDetailsError: '',
            secureShareSubmitting: false,
            secureShareError: '',
            editDetailsForm: {
                title: '',
                short_description: '',
            },
            secureShareForm: {
                recipient_email: '',
                expires_in: '3d',
            },
            lockStateIntervalId: null,
            accessStateRequestInFlight: false,
            redirectedByLockState: false,
            visibilityListener: null,
            vault: createDefaultVaultState(),
            vaultSecretText: '',
            historyEntries: [],
            readerOptions: [],
            customerOptions: [],
            guestOptions: [],
        };
    },
    computed: {
        canEdit() {
            return !!this.vault.can_edit;
        },
        canDelete() {
            return !!this.vault.can_delete;
        },
        canManageDetails() {
            return !!this.vault.can_manage_details;
        },
        canManagePermissions() {
            return !!this.vault.can_manage_permissions;
        },
        accessState() {
            return resolveAccessState(this.$store);
        },
        isAdmin() {
            return this.accessState.isAdmin;
        },
        isCustomer() {
            return this.accessState.isCustomer;
        },
        currentUserEmail() {
            const user = this.$store && this.$store.state ? this.$store.state.user : null;
            return user && user.email ? String(user.email) : '';
        },
        currentUserAvatar() {
            const user = this.$store && this.$store.state ? this.$store.state.user : null;
            if (!user) {
                return 'https://i.pravatar.cc/64?img=1';
            }

            if (user.avatar && user.avatar !== 'gravatar') {
                return user.avatar;
            }

            if (user.gravatar) {
                return user.gravatar;
            }

            return 'https://i.pravatar.cc/64?img=1';
        },
        unlockCodeCooldownRemaining() {
            if (!this.unlockCodeCooldownUntil) {
                return 0;
            }
            const remainingMs = this.unlockCodeCooldownUntil - this.nowTick;
            if (remainingMs <= 0) {
                return 0;
            }
            return Math.ceil(remainingMs / 1000);
        },
        lineNumbers() {
            const lineCount = Math.max(1, String(this.vaultSecretText || '').split('\n').length);
            return Array.from({length: lineCount}, (_, index) => index + 1);
        },
        vaultBasePath() {
            return this.$route.path.startsWith('/workspace/vault') ? '/workspace/vault' : '/vault';
        },
        apiVaultBasePath() {
            return this.$route.path.startsWith('/workspace/vault') ? 'api/workspace/vault' : 'api/vault';
        },
        vaultListPath() {
            const fallbackPath = this.vaultBasePath === '/workspace/vault' ? '/workspace/vault' : '/vault/list';
            return resolveReturnToPath(this.$route.query || {}, fallbackPath);
        },
        vaultOwnerAvatar() {
            if (this.vault && this.vault.owner_avatar) {
                return this.vault.owner_avatar;
            }

            return 'https://i.pravatar.cc/64?img=1';
        },
        vaultInternalReleaseUsers() {
            return buildVaultReleaseUsers(this.vault && this.vault.readers);
        },
        vaultCustomerReleaseUsers() {
            return buildVaultReleaseUsers(this.vault && this.vault.customers);
        },
        vaultGuestReleaseUsers() {
            return buildVaultReleaseUsers(this.vault && this.vault.guests);
        },
    },
    mounted() {
        this.loadVault();
        this.clockIntervalId = window.setInterval(() => {
            this.nowTick = Date.now();
        }, 1000);
        this.lockStateIntervalId = window.setInterval(() => {
            this.checkVaultAccessState();
        }, 5000);
        this.visibilityListener = () => {
            if (document.visibilityState === 'visible') {
                this.checkVaultAccessState();
            }
        };
        document.addEventListener('visibilitychange', this.visibilityListener);
    },
    async beforeRouteLeave() {
        this.redirectedByLockState = true;
        await this.lockVaultOnLeave();
        return true;
    },
    beforeUnmount() {
        if (this.clockIntervalId) {
            window.clearInterval(this.clockIntervalId);
            this.clockIntervalId = null;
        }
        if (this.lockStateIntervalId) {
            window.clearInterval(this.lockStateIntervalId);
            this.lockStateIntervalId = null;
        }
        if (this.visibilityListener) {
            document.removeEventListener('visibilitychange', this.visibilityListener);
            this.visibilityListener = null;
        }
    },
    filters: {
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    methods: {
        lockVaultOnLeave() {
            if (this.lockingVaultOnLeave) {
                return Promise.resolve();
            }

            if (!this.vault.id) {
                return Promise.resolve();
            }

            this.lockingVaultOnLeave = true;
            return axios.post(this.apiVaultBasePath + '/' + this.vault.id + '/lock')
                .catch(() => {
                    // Do not block route change even if lock request fails.
                })
                .finally(() => {
                    this.lockingVaultOnLeave = false;
                });
        },
        checkVaultAccessState() {
            if (!this.vault.id || this.loading || this.lockingVaultOnLeave || this.redirectedByLockState || this.accessStateRequestInFlight) {
                return;
            }

            const self = this;
            self.accessStateRequestInFlight = true;
            axios.get(self.apiVaultBasePath + '/' + self.$route.params.id + '/access-state')
                .then(function (response) {
                    const unlocked = !!(response.data && response.data.unlocked);
                    if (!unlocked) {
                        self.handleVaultLockedElsewhere();
                    }
                })
                .catch(function (error) {
                    const status = error && error.response ? Number(error.response.status) : 0;
                    if (status === 401 || status === 403 || status === 423) {
                        self.handleVaultLockedElsewhere();
                    }
                })
                .finally(function () {
                    self.accessStateRequestInFlight = false;
                });
        },
        handleVaultLockedElsewhere() {
            if (this.redirectedByLockState) {
                return;
            }

            this.redirectedByLockState = true;
            this.closeActionDropdowns();
            this.secureShareModalOpen = false;
            this.unlockModalOpen = false;
            this.deleteNoteModalOpen = false;
            this.editDetailsModalOpen = false;
            this.$notify({
                title: this.$i18n.t('Error').toString(),
                text: this.$t('Vault was locked. Please open the note again via the overview').toString(),
                type: 'warning'
            });
            this.$router.replace(this.vaultListPath);
        },
        closeActionDropdowns() {
            this.historyPopoverOpen = false;
        },
        toggleHistoryPopover() {
            const wasOpen = this.historyPopoverOpen;
            this.closeActionDropdowns();
            this.historyPopoverOpen = !wasOpen;
        },
        openApprovalModal() {
            if (!this.canManagePermissions || this.isCustomer) {
                return;
            }

            this.approvalModal = true;
        },
        closeApprovalModal() {
            this.approvalModal = false;
        },
        loadVault() {
            const self = this;
            self.loading = true;
            axios.get(self.apiVaultBasePath + '/' + self.$route.params.id).then(function (response) {
                self.redirectedByLockState = false;
                const vault = response.data || {};
                self.vault = normalizeVaultState(vault, createDefaultVaultState());
                self.vaultSecretText = String(vault.content || '');
                self.historyEntries = Array.isArray(vault.activity_history) ? vault.activity_history : [];
                self.loadReleaseOptions();
                self.loading = false;
            }).catch(function (error) {
                self.loading = false;
                const status = error && error.response ? Number(error.response.status) : 0;
                if (status === 423) {
                    self.handleVaultLockedElsewhere();
                    return;
                }
                self.$router.push(self.vaultListPath);
            });
        },
        saveVaultEntry() {
            if (!this.canEdit) {
                return;
            }
            this.updateVault({
                content: this.vaultSecretText,
            }, 'Vault-Eintrag wurde gespeichert.');
        },
        loadReleaseOptions() {
            const self = this;
            if (self.canManagePermissions && !self.isCustomer) {
                axios.get(self.apiVaultBasePath + '/readers').then(function (response) {
                    self.readerOptions = response.data || [];
                }).catch(function () {
                    self.readerOptions = [];
                });
            } else {
                self.readerOptions = [];
            }

            if (self.isAdmin) {
                axios.get(self.apiVaultBasePath + '/customers').then(function (response) {
                    self.customerOptions = response.data || [];
                }).catch(function () {
                    self.customerOptions = [];
                });

                axios.get(self.apiVaultBasePath + '/guests').then(function (response) {
                    self.guestOptions = response.data || [];
                }).catch(function () {
                    self.guestOptions = [];
                });
            } else {
                self.customerOptions = [];
                self.guestOptions = [];
            }
        },
        saveApprovalSelections(payload) {
            if (!this.canManagePermissions || this.isCustomer) {
                return;
            }

            this.vault.reader_ids = Array.isArray(payload && payload.internal_ids) ? payload.internal_ids : [];
            this.vault.customer_ids = Array.isArray(payload && payload.customer_ids) ? payload.customer_ids : [];
            this.vault.guest_ids = Array.isArray(payload && payload.guest_ids) ? payload.guest_ids : [];

            this.updateVault(
                {
                    reader_ids: this.vault.reader_ids,
                    customer_ids: this.vault.customer_ids,
                    guest_ids: this.vault.guest_ids,
                },
                'Freigaben aktualisiert.',
                () => {
                    this.approvalModal = false;
                }
            );
        },
        resolveAvatar: resolveVaultAvatar,
        updateVault(payload, successText, onSuccess = null, onError = null) {
            const self = this;
            if (self.savingEntry) {
                return;
            }
            self.savingEntry = true;
            axios.put(self.apiVaultBasePath + '/' + self.$route.params.id, payload).then(function (response) {
                const vaultData = response.data && response.data.vault ? response.data.vault : null;
                if (vaultData) {
                    self.vault = normalizeVaultState(vaultData, self.vault);
                    self.vaultSecretText = String(vaultData.content || '');
                    self.historyEntries = Array.isArray(vaultData.activity_history) ? vaultData.activity_history : self.historyEntries;
                }
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: successText,
                    type: 'success'
                });
                if (typeof onSuccess === 'function') {
                    onSuccess(response);
                }
            }).catch(function (error) {
                const errorMessage = self.getApiErrorMessage(error, 'Speichern fehlgeschlagen.');
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: errorMessage,
                    type: 'error'
                });
                if (typeof onError === 'function') {
                    onError(errorMessage, error);
                }
                const status = error && error.response ? Number(error.response.status) : 0;
                if (status === 423) {
                    self.$router.push(self.vaultListPath);
                }
            }).finally(function () {
                self.savingEntry = false;
                self.editDetailsSubmitting = false;
            });
        },
        getApiErrorMessage(error, fallback) {
            if (error && error.response && error.response.data) {
                const responseData = error.response.data;
                if (responseData.message) {
                    return responseData.message;
                }
                if (responseData.errors && typeof responseData.errors === 'object') {
                    const firstField = Object.keys(responseData.errors)[0];
                    if (firstField && responseData.errors[firstField] && responseData.errors[firstField][0]) {
                        return responseData.errors[firstField][0];
                    }
                }
            }

            return fallback;
        },
        openDeleteUnlockModal() {
            this.unlockError = '';
            this.unlockCodeDigits = ['', '', '', ''];
            this.unlockModalOpen = true;
            this.$nextTick(() => {
                const inputs = this.$refs.unlockCodeInput || [];
                if (inputs[0]) {
                    inputs[0].focus();
                }
            });
        },
        closeUnlockModal() {
            this.unlockModalOpen = false;
            this.unlockCodeDigits = ['', '', '', ''];
            this.unlockError = '';
        },
        handleCodeInput(index, event) {
            if (this.unlockSubmitting) {
                return;
            }
            const rawValue = event && event.target ? String(event.target.value || '') : '';
            const digits = rawValue.replace(/\D/g, '');
            if (!digits) {
                this.$set(this.unlockCodeDigits, index, '');
                return;
            }

            if (digits.length > 1) {
                this.applyPastedCode(digits);
                return;
            }

            this.unlockError = '';
            this.$set(this.unlockCodeDigits, index, digits);
            if (index < this.unlockCodeDigits.length - 1) {
                this.focusCodeInput(index + 1);
            }
            this.maybeAutoConfirmUnlockForDelete();
        },
        handleCodeKeydown(index, event) {
            const key = event && event.key ? event.key : '';
            if (key === 'Backspace' && !this.unlockCodeDigits[index] && index > 0) {
                this.focusCodeInput(index - 1);
            }
            if (key === 'ArrowLeft' && index > 0) {
                event.preventDefault();
                this.focusCodeInput(index - 1);
            }
            if (key === 'ArrowRight' && index < this.unlockCodeDigits.length - 1) {
                event.preventDefault();
                this.focusCodeInput(index + 1);
            }
        },
        handleCodePaste(event) {
            if (this.unlockSubmitting) {
                return;
            }
            const pastedText = event && event.clipboardData ? event.clipboardData.getData('text') : '';
            const digits = String(pastedText || '').replace(/\D/g, '');
            if (!digits) {
                return;
            }
            event.preventDefault();
            this.applyPastedCode(digits);
        },
        applyPastedCode(digits) {
            const normalized = String(digits || '').slice(0, 4);
            this.unlockError = '';
            for (let i = 0; i < this.unlockCodeDigits.length; i += 1) {
                this.$set(this.unlockCodeDigits, i, normalized[i] || '');
            }

            const focusIndex = Math.min(normalized.length, this.unlockCodeDigits.length - 1);
            this.focusCodeInput(focusIndex);
            this.maybeAutoConfirmUnlockForDelete();
        },
        maybeAutoConfirmUnlockForDelete() {
            if (this.unlockSubmitting) {
                return;
            }

            if (this.unlockCodeDigits.some((digit) => digit === '')) {
                return;
            }

            this.confirmUnlockForDelete();
        },
        focusCodeInput(index) {
            this.$nextTick(() => {
                const inputs = this.$refs.unlockCodeInput || [];
                if (inputs[index]) {
                    inputs[index].focus();
                    inputs[index].select();
                }
            });
        },
        enteredUnlockCode() {
            return this.unlockCodeDigits.join('');
        },
        sendUnlockCodeByEmail() {
            if (this.sendingUnlockCode || this.unlockCodeCooldownRemaining > 0) {
                return;
            }

            const self = this;
            this.sendingUnlockCode = true;
            this.unlockError = '';

            axios.post(self.apiVaultBasePath + '/send-access-code', {
                vault_id: this.$route.params.id
            }).then(function (response) {
                const cooldownSeconds = Number(response.data && response.data.cooldown_seconds ? response.data.cooldown_seconds : 30);
                self.unlockCodeCooldownUntil = Date.now() + (cooldownSeconds * 1000);
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: response.data && response.data.message
                        ? response.data.message
                        : 'Einmal-Code wurde per E-Mail gesendet.',
                    type: 'success'
                });
            }).catch(function (error) {
                const cooldownSeconds = Number(
                    error && error.response && error.response.data && error.response.data.cooldown_seconds
                        ? error.response.data.cooldown_seconds
                        : 0
                );
                if (cooldownSeconds > 0) {
                    self.unlockCodeCooldownUntil = Date.now() + (cooldownSeconds * 1000);
                }
                self.unlockError = self.getApiErrorMessage(error, self.$t('Code could not be sent').toString());
            }).finally(function () {
                self.sendingUnlockCode = false;
            });
        },
        confirmUnlockForDelete() {
            if (this.unlockSubmitting) {
                return;
            }
            const enteredCode = this.enteredUnlockCode();
            if (!enteredCode || enteredCode.length !== 4) {
                return;
            }

            const self = this;
            self.unlockSubmitting = true;
            self.unlockError = '';
            axios.post(self.apiVaultBasePath + '/verify-access-code', {
                vault_id: self.$route.params.id,
                code: enteredCode
            }).then(function () {
                self.closeUnlockModal();
                self.deleteNoteModalOpen = true;
            }).catch(function (error) {
                self.unlockCodeDigits = ['', '', '', ''];
                self.focusCodeInput(0);
                const remainingAttempts = Number(
                    error && error.response && error.response.data && error.response.data.remaining_attempts
                        ? error.response.data.remaining_attempts
                        : NaN
                );
                if (!Number.isNaN(remainingAttempts)) {
                    self.unlockError = self.$t('Code is invalid (:attempts attempts left)', {attempts: remainingAttempts}).toString();
                    return;
                }
                self.unlockError = self.getApiErrorMessage(error, self.$t('The entered code is invalid').toString());
            }).finally(function () {
                self.unlockSubmitting = false;
            });
        },
        deleteNote() {
            const self = this;
            self.deleteNoteModalOpen = false;
            self.loading = true;
            axios.delete(self.apiVaultBasePath + '/' + self.$route.params.id).then(function (response) {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: response.data && response.data.message ? response.data.message : self.$t('Note was deleted').toString(),
                    type: 'success'
                });
                self.vault.id = null;
                self.$router.replace(self.vaultListPath);
            }).catch(function (error) {
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.getApiErrorMessage(error, self.$t('Note could not be deleted').toString()),
                    type: 'error'
                });
            });
        },
        openDeleteConfirmationModal() {
            this.deleteNoteModalOpen = true;
        },
        openTitleModal() {
            this.editDetailsError = '';
            this.editDetailsForm = {
                title: String(this.vault.title || ''),
                short_description: String(this.vault.description || ''),
            };
            this.editDetailsModalOpen = true;
            this.$nextTick(() => {
                if (this.canManageDetails && this.$refs.editDetailsTitleInput) {
                    this.$refs.editDetailsTitleInput.focus();
                    this.$refs.editDetailsTitleInput.select();
                }
            });
        },
        closeEditDetailsModal() {
            this.editDetailsModalOpen = false;
            this.editDetailsSubmitting = false;
            this.editDetailsError = '';
        },
        saveVaultDetails() {
            if (this.editDetailsSubmitting) {
                return;
            }

            const payload = {};
            if (this.canManageDetails) {
                if (!this.editDetailsForm.title || this.editDetailsForm.title.trim().length < 3) {
                    this.editDetailsError = 'Der Titel muss mindestens 3 Zeichen enthalten.';
                    return;
                }
                payload.title = this.editDetailsForm.title.trim();
            }

            if (this.canEdit) {
                payload.short_description = this.editDetailsForm.short_description;
            }

            this.editDetailsSubmitting = true;
            this.editDetailsError = '';

            this.updateVault(
                payload,
                'Notizdetails wurden gespeichert.',
                () => {
                    this.closeEditDetailsModal();
                },
                (errorMessage) => {
                    this.editDetailsError = errorMessage;
                }
            );
        },
        openSecureShareModal() {
            this.secureShareError = '';
            this.secureShareForm.recipient_email = this.vault.active_secure_share
                ? String(this.vault.active_secure_share.recipient_email || '')
                : '';
            this.secureShareForm.expires_in = '3d';
            this.secureShareModalOpen = true;
            this.$nextTick(() => {
                if (this.$refs.secureShareEmailInput) {
                    this.$refs.secureShareEmailInput.focus();
                }
            });
        },
        closeSecureShareModal() {
            this.secureShareModalOpen = false;
            this.secureShareError = '';
        },
        sendSecureShareLink() {
            if (this.secureShareSubmitting || !this.vault.id) {
                return;
            }

            const self = this;
            self.secureShareSubmitting = true;
            self.secureShareError = '';
            axios.post(self.apiVaultBasePath + '/' + self.$route.params.id + '/secure-share', self.secureShareForm)
                .then(function (response) {
                    self.$notify({
                        title: self.$i18n.t('Success').toString(),
                        text: response.data && response.data.message ? response.data.message : self.$t('Note was sent securely').toString(),
                        type: 'success'
                    });
                    self.secureShareModalOpen = false;
                    self.loadVault();
                }).catch(function (error) {
                    const status = error && error.response ? Number(error.response.status) : 0;
                    if (status === 423) {
                        self.handleVaultLockedElsewhere();
                        return;
                    }
                    self.secureShareError = self.getApiErrorMessage(error, 'Versand fehlgeschlagen.');
                }).finally(function () {
                    self.secureShareSubmitting = false;
                });
        },
        revokeActiveSecureShare() {
            if (!this.canManagePermissions || this.revokingSecureShare || !this.vault.id) {
                return;
            }

            const self = this;
            self.revokingSecureShare = true;
            axios.post(self.apiVaultBasePath + '/' + self.$route.params.id + '/secure-share/revoke')
                .then(function (response) {
                    self.$notify({
                        title: self.$i18n.t('Success').toString(),
                        text: response.data && response.data.message ? response.data.message : 'Sicherer Versand wurde deaktiviert.',
                        type: 'success'
                    });
                    self.loadVault();
                }).catch(function (error) {
                    self.$notify({
                        title: self.$i18n.t('Error').toString(),
                        text: self.getApiErrorMessage(error, self.$t('Secure sharing could not be deactivated').toString()),
                        type: 'error'
                    });
                    const status = error && error.response ? Number(error.response.status) : 0;
                    if (status === 423) {
                        self.handleVaultLockedElsewhere();
                    }
                }).finally(function () {
                    self.revokingSecureShare = false;
                });
        },
        syncEditorScroll() {
            if (!this.$refs.vaultEditor || !this.$refs.lineNumberGutter) {
                return;
            }
            this.$refs.lineNumberGutter.scrollTop = this.$refs.vaultEditor.scrollTop;
        },
        formatDateTime: formatVaultDateTime,
        goBackToVaultList() {
            this.$router.push(this.vaultListPath);
        },
        historyAccessType: resolveVaultHistoryAccessType,
    }
};
</script>

<style scoped>
.vault-history-popover {
    max-height: 430px;
}

.vault-editor-shell {
    display: flex;
    width: 100%;
    height: 100%;
    background: #ffffff;
}

.vault-editor-panel {
    flex: 1 1 auto;
    min-height: 0;
}

.vault-secure-share-panel {
    border-top: 1px solid var(--ui-border-subtle);
}

.vault-editor-header {
    background: var(--ui-surface-raised);
    border-bottom: 1px solid var(--ui-border-subtle);
}

.vault-editor-header-gutter {
    background: var(--ui-surface-raised);
    border-right: 1px solid var(--ui-border-subtle);
}

.vault-editor-header-save {
    border-left: 1px solid var(--ui-border-subtle);
}

.vault-editor-gutter {
    flex: 0 0 56px;
    width: 56px;
    overflow: hidden;
    background: var(--ui-surface-raised);
    border-right: 1px solid var(--ui-border-subtle);
    color: var(--ui-text-muted);
    font-family: Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 12px;
    line-height: 1.55;
    padding: 12px 8px 12px 0;
    text-align: right;
    user-select: none;
}

.vault-editor-line-number {
    height: 1.55em;
}

.vault-editor-textarea {
    flex: 1 1 auto;
    width: 100%;
    border: 0;
    outline: 0;
    resize: none;
    background: #ffffff;
    color: var(--ui-text-heading);
    font-family: Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 13px;
    line-height: 1.55;
    padding: 12px 14px;
    overflow: auto;
    white-space: pre;
}

.vault-editor-textarea[readonly] {
    background: #ffffff;
    color: var(--ui-text-strong);
}

.vault-otp-group {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0;
}

.vault-otp-input {
    height: 3rem;
    border: 1px solid var(--ui-border-default);
    border-left: 0;
    text-align: center;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--ui-text-strong);
    outline: none;
    border-radius: 0 !important;
}

.vault-otp-input:first-child {
    border-left: 1px solid var(--ui-border-default);
    border-top-left-radius: 0.375rem !important;
    border-bottom-left-radius: 0.375rem !important;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}

.vault-otp-input:not(:first-child):not(:last-child) {
    border-radius: 0 !important;
}

.vault-otp-input:last-child {
    border-top-right-radius: 0.375rem !important;
    border-bottom-right-radius: 0.375rem !important;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}

.vault-otp-input:focus {
    border-color: var(--ui-text-subtle);
    position: relative;
    z-index: 1;
}

</style>
